import React, { useState, useMemo, useContext, useEffect } from "react";
import { Flex, TreeSelect, DatePicker } from "antd";
import dayjs from "dayjs";
import { AppContext } from "../../../context/AppContext";
import { useListViewStore } from "../../../pages/listViewStore";
import { DEFAULT_CONTACT_ID } from "../../../pages/ConversationContext";
import LabelAndValue from "../../custom/form/LabelAndValue";
import Checkbox from "../../custom/input/checkbox/Checkbox";
import HSelect from "../../custom/input/select/Select";
import { default as HSButton } from "../../custom/input/button/Button";
import { capitalizeFirstLetter } from "../../../pages/utils/commonUtils";
import { INBOX_DEFAULT_FILTERS } from "../../../constants/AppConstants";
import { useLocation, useNavigate } from "react-router-dom";

const { SHOW_PARENT } = TreeSelect;
const { RangePicker } = DatePicker;

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getFlagColors = (size = "medium") => {
  const FLAG_COLORS = [
    {
      key: "red",
      color: "red",
      outlineIcon: (
        <div
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-red-outlined${
            size === "large" ? "-large" : ""
          }`}
        />
      ),
    },
    {
      key: "orange",
      color: "orange",
      outlineIcon: (
        <div
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-orange-outlined${
            size === "large" ? "-large" : ""
          }`}
        />
      ),
    },
    {
      key: "yellow",
      color: "yellow",
      outlineIcon: (
        <div
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-yellow-outlined${
            size === "large" ? "-large" : ""
          }`}
        />
      ),
    },
    {
      key: "green",
      color: "green",
      outlineIcon: (
        <div
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-green-outlined${
            size === "large" ? "-large" : ""
          }`}
        />
      ),
    },
  ];
  return FLAG_COLORS;
};

const InboxFilterPopoverCard = ({
  services,
  setIsChannelPresent,
  setSelectedContact,
  selectedContactId,
  setPopoverOpen,
  popoverOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appProperties] = useContext(AppContext);
  const {
    activeTab,
    selectedChannels,
    setSelectedChannels,
    selectedFlagFilter,
    setSelectedFlagFilter,
    selectedAssignees,
    setSelectedAssignees,
    selectedReadStatus,
    setSelectedReadStatus,
    selectedConversationStatus,
    setSelectedConversationStatus,
    selectedDateRange,
    setSelectedDateRange,
  } = useListViewStore();

  // Local state for temporary filter values (before applying)
  const [tempSelectedChannels, setTempSelectedChannels] = useState(selectedChannels && selectedChannels.length > 0 ? selectedChannels : []);
  const [tempSelectedFlagFilter, setTempSelectedFlagFilter] = useState(() => toArray(selectedFlagFilter));
  const [tempSelectedAssignees, setTempSelectedAssignees] = useState(() => toArray(selectedAssignees));
  const [tempSelectedReadStatus, setTempSelectedReadStatus] = useState(selectedReadStatus || "all");
  const [tempSelectedConversationStatus, setTempSelectedConversationStatus] = useState(() => toArray(selectedConversationStatus));
  const [tempSelectedDateRange, setTempSelectedDateRange] = useState(selectedDateRange || null);

  // Sync temporary state with store values when popover opens or store values change
  useEffect(() => {
    if (popoverOpen) {
      setTempSelectedChannels(selectedChannels && selectedChannels.length > 0 ? selectedChannels : []);
      setTempSelectedFlagFilter(toArray(selectedFlagFilter));
      setTempSelectedAssignees(toArray(selectedAssignees));
      setTempSelectedReadStatus(selectedReadStatus || "all");

      // For conversation status: filter out "unread" as it's handled via tabs only
      const initialConversationStatus = toArray(selectedConversationStatus).filter((status) => status !== "unread");
      setTempSelectedConversationStatus(initialConversationStatus);
      
      setTempSelectedDateRange(selectedDateRange || null);
    }
  }, [popoverOpen, selectedChannels, selectedFlagFilter, selectedAssignees, selectedReadStatus, selectedConversationStatus, selectedDateRange, activeTab]);

  // Prepare assignee options for the filter (only for InboxFilterPopoverCard - show name (email) or just email)
  const assigneeOptions = useMemo(
    () => [
      ...(appProperties?.licenseObj?.allLicensedUserDetails?.map((user) => {
        // Show "Name (email)" if both present, otherwise just email
        const hasName = user?.firstName || user?.lastName;
        const name = hasName ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim() : "";
        const email = user?.email || "";
        const displayName = hasName && email ? `${name} (${email})` : email || name;
        return {
          ...user,
          label: displayName, // String label for tag rendering
          labelText: displayName, // For search functionality
          value: user.osyncUserId,
          userData: user,
        };
      }) || []),
    ],
    [appProperties?.licenseObj?.allLicensedUserDetails]
  );

  const handleSaveChanges = () => {
    // Update the store with multi-select arrays
    setSelectedChannels(tempSelectedChannels);
    setSelectedFlagFilter(toArray(tempSelectedFlagFilter));
    setSelectedAssignees(toArray(tempSelectedAssignees));
    // Set read status - clear if on "unread" tab (it's handled by tab config)
    if (activeTab === "unread") {
      setSelectedReadStatus("all"); // Tab handles it
    } else {
      setSelectedReadStatus(tempSelectedReadStatus || "all");
    }
    // Set conversation status - filter out "unread" as it's only handled via tabs
    const finalConversationStatus = toArray(tempSelectedConversationStatus).filter((status) => status !== "unread");
    setSelectedConversationStatus(finalConversationStatus);
    // Set date range
    setSelectedDateRange(tempSelectedDateRange);

    // Reset contact selection
    setIsChannelPresent(false);
    setSelectedContact(null);
    selectedContactId.current = DEFAULT_CONTACT_ID;

    navigate(`/inbox${location.search}`);

    // Close the popover
    setPopoverOpen(false);
  };

  const handleDateRangeChange = (dates) => {
    setTempSelectedDateRange(dates);
  };

  return (
    <Flex vertical gap="middle">
      <div>
        <LabelAndValue label={"Channel"}>
          <TreeSelect
            tabIndex={2}
            treeCheckable={true}
            showCheckedStrategy={SHOW_PARENT}
            suffixIcon={<div className="actionIconsSprite fromNumberDropDownIcon" />}
            placeholder="All Services"
            value={tempSelectedChannels}
            switcherIcon={({ expanded }) => (
              <div
                data-cy="filter-inside-right-icon"
                className={`actionIconsSprite click-to-switcher-right-icon ${expanded ? "click-to-switcher-right-icon-rotate-down" : ""}`}
              />
            )}
            placement="bottomLeft"
            dropdownStyle={{ width: 180 }}
            popupMatchSelectWidth={false}
            data-cy="inbox-filter"
            showSearch={true}
            treeNodeFilterProp="title"
            filterTreeNode={(inputValue, treeNode) => {
              const title = treeNode.title?.props?.children || treeNode.title || "";
              const friendlyName = treeNode.friendlyName || "";
              const teamName = treeNode.teamName || "";
              const phoneNumber = treeNode.phoneNumber || "";

              const searchText = inputValue.toLowerCase();
              const titleMatch = title.toLowerCase().includes(searchText);
              const friendlyNameMatch = friendlyName.toLowerCase().includes(searchText);
              const teamNameMatch = teamName.toLowerCase().includes(searchText);
              const phoneNumberMatch = phoneNumber.toLowerCase().includes(searchText);

              return titleMatch || friendlyNameMatch || teamNameMatch || phoneNumberMatch;
            }}
            onChange={(value) => {
              setTempSelectedChannels(value || []);
            }}
            treeData={services
              .filter((service) => service.value !== "All")
              .map((service) => ({
                ...service,
                title: (
                  <span data-cy={`inbox-filter-option-${service.title?.toString().toLowerCase().replace(/ /g, "_")}`}>{service.title}</span>
                ),
                children: service.children?.map((child) => ({
                  ...child,
                  title: (
                    <span className={/^\+?\d+$/.test(child.title) ? "connected-number" : ""} data-cy="connected-number">
                      {child.title}
                    </span>
                  ),
                })),
              }))}
            className="h-100 hs-input-box w-auto"
            rootClassName="channel-dropdown overflow-hidden"
          />
        </LabelAndValue>
      </div>
      <div>
        <LabelAndValue label="Date Range">
          <RangePicker
            data-cy="inbox-filter-date-range"
            format="YYYY-MM-DD"
            showTime
            value={tempSelectedDateRange}
            onChange={handleDateRangeChange}
            disabledDate={(current) => {
              const isAfterToday = current && current > dayjs().endOf("day");
              return isAfterToday;
            }}
            className="w-100"
            popupClassName="inbox-filter-date-picker-dropdown"
            rootClassName="date-range-picker"
          />
        </LabelAndValue>
      </div>
      {/* Status filter: different options based on tab */}
      {!INBOX_DEFAULT_FILTERS[activeTab]?.disabledFilters?.includes("status") && (
        <LabelAndValue label="Status">
          <div className="d-flex gap-2">
            {Object.values(appProperties?.enums?.conversationStatusType).map((status) => (
              <Checkbox
                data-cy={`inbox-filter-status-${(status.code || "").toLowerCase()}`}
                checked={tempSelectedConversationStatus.includes(status.code)}
                item={{ label: status.activeLabel, value: status.code }}
                onChange={(e) =>
                  setTempSelectedConversationStatus((prev) =>
                    e.target.checked ? [...prev, status.code] : prev.filter((code) => code !== status.code)
                  )
                }
              />
            ))}
          </div>
        </LabelAndValue>
      )}
      <div>
        <LabelAndValue label="Flagged">
          <div className="d-flex gap-2 w-auto justify-content-between">
            {getFlagColors("medium").map((flag) => (
              <Checkbox
                data-cy={`inbox-filter-flag-${flag.color}`}
                rounded={false}
                checked={tempSelectedFlagFilter.includes(flag.color)}
                item={{ label: capitalizeFirstLetter(flag.color), value: flag.color }}
                onChange={(e) =>
                  setTempSelectedFlagFilter((prev) =>
                    prev.includes(flag.color) ? prev.filter((color) => color !== flag.color) : [...prev, flag.color]
                  )
                }
              />
            ))}
          </div>
        </LabelAndValue>
      </div>
      {!INBOX_DEFAULT_FILTERS[activeTab]?.disabledFilters?.includes("assignee") && (
        <div className="overflow-auto px-1">
          <LabelAndValue label="Assigned To">
            <HSelect
              data-cy="inbox-filter-assigned-users"
              bordered={false}
              mode="multiple"
              size="medium"
              customProps={{
                value: tempSelectedAssignees,
                onChange: (value) => setTempSelectedAssignees(toArray(value)),
                className: "overflow-hidden",
                showSearch: false,
              }}
              defaultTagRender={{
                onCloseSetState: setTempSelectedAssignees,
                tagItemToRender: "label",
              }}
              popupMatchSelectWidth={false}
              placeholder="Select Assignees"
              options={assigneeOptions}
              loading={false}
              rootClassName="assignee-dropdown"
            />
          </LabelAndValue>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center ms-1">
        <div
          data-cy="inbox-filter-reset-button"
          className="hs-link"
          onClick={() => {
            // Reset only temporary filter values in the popover (not the store)
            setTempSelectedChannels([]);
            setTempSelectedFlagFilter([]);
            setTempSelectedAssignees([]);
            setTempSelectedReadStatus("all");
            setTempSelectedConversationStatus([]);
            setTempSelectedDateRange(null);
          }}
        >
          Reset Filters
        </div>
        <HSButton data-cy="inbox-filter-apply-button" size="s" type="primary" onClick={handleSaveChanges}>
          Apply Filters
        </HSButton>
      </div>
    </Flex>
  );
};

export default InboxFilterPopoverCard;

